import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PaginatedResponse, ProductComplaint } from '../types';
import { fetchProductComplaints, resolveProductComplaint } from '../services/api';
import { encodeId } from '../utils/hashids';
import Pager from '../components/Pager';

const STATUS_LABELS: Record<string, string> = {
  open: 'Open', investigating: 'Investigating', resolved: 'Resolved', rejected: 'Rejected',
};

/**
 * Phase 3.5 — product defect complaints, following TasksPage's general
 * list+filter+row-action shape (complaints filter on discrete fields —
 * status/product/batch — rather than dates, so no date-bucketing here).
 * A product_defect issue always raises one of these, never a sales task.
 */
const ComplaintsPage: React.FC = () => {
  const history = useHistory();
  const [data, setData]       = useState<PaginatedResponse<ProductComplaint> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [page, setPage]       = useState(1);
  const [statusFilter, setStatusFilter]     = useState('');
  const [productFilter, setProductFilter]   = useState('');
  const [batchFilter, setBatchFilter]       = useState('');
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchProductComplaints({
        page,
        status: statusFilter || undefined,
        product_name: productFilter || undefined,
        batch_ref: batchFilter || undefined,
      });
      setData(res);
    } catch {
      setError('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, productFilter, batchFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [statusFilter, productFilter, batchFilter]);

  const handleResolve = async (complaint: ProductComplaint, status: 'resolved' | 'rejected') => {
    setWorking(true);
    try { await resolveProductComplaint(complaint.id, status); await load(); }
    catch { alert('Failed to update complaint'); }
    finally { setWorking(false); }
  };

  return (
    <div className="list-page">
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">Product Complaints</h1>
        </div>
      </div>

      <div className="filter-bar">
        <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input
          className="filter-input"
          placeholder="Filter by product…"
          value={productFilter}
          onChange={e => setProductFilter(e.target.value)}
        />
        <input
          className="filter-input"
          placeholder="Filter by batch ref…"
          value={batchFilter}
          onChange={e => setBatchFilter(e.target.value)}
        />
      </div>

      <div className="data-card">
        {loading ? (
          <div className="spinner">Loading…</div>
        ) : error ? (
          <div className="spinner">{error}</div>
        ) : (
          <div className="data-card-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Product</th>
                  <th>Batch</th>
                  <th>Severity</th>
                  <th>Complaint</th>
                  <th>Status</th>
                  <th>Reported</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data && data.data.length > 0 ? data.data.map(c => (
                  <tr key={c.id}>
                    <td>
                      <a
                        href="#"
                        onClick={e => { e.preventDefault(); history.push(`/leads/${encodeId(c.reporter_lead_id)}`); }}
                      >
                        {c.reporter_lead?.name ?? `Lead #${c.reporter_lead_id}`}
                      </a>
                    </td>
                    <td>{c.product_name ?? '—'}</td>
                    <td>{c.batch_ref ?? '—'}</td>
                    <td>{c.severity ?? '—'}</td>
                    <td style={{ maxWidth: 320 }}>
                      <span title={c.complaint_text ?? ''} style={{
                        display: 'block', overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', fontSize: 12,
                      }}>
                        {c.complaint_text ?? '—'}
                      </span>
                    </td>
                    <td><span className={`status-badge status-${c.status}`}>{STATUS_LABELS[c.status]}</span></td>
                    <td>{c.reported_at ? new Date(c.reported_at).toLocaleDateString() : '—'}</td>
                    <td>
                      {(c.status === 'open' || c.status === 'investigating') ? (
                        <div className="row-actions">
                          <button className="btn btn-primary btn-sm" disabled={working} onClick={() => handleResolve(c, 'resolved')}>Resolve</button>
                          <button className="btn btn-secondary btn-sm" disabled={working} onClick={() => handleResolve(c, 'rejected')}>Reject</button>
                        </div>
                      ) : (
                        <span className="td-muted">—</span>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr className="table-empty-row">
                    <td colSpan={8}>No product complaints match these filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {data && data.data.length > 0 && (
        <Pager
          currentPage={data.current_page}
          lastPage={data.last_page}
          total={data.total}
          label="Complaints"
          onChange={setPage}
        />
      )}
    </div>
  );
};

export default ComplaintsPage;
